<template>
    <kiosk-layout>
        <div>
            <page-callout section="form-submission" class="mb-4" shadow />

            <page-loading v-if="loading"></page-loading>

            <div v-else-if="loadError" data-test="load-error">
                <vx-alert color="danger" :closeable="false">{{ loadError }}</vx-alert>

                <vx-button href="/kiosk/inbound-text/links" color="muted-dark" size="lg" class="mt-4" data-test="back">
                    Back to Links
                </vx-button>
            </div>

            <template v-else-if="details">
                <page-header>
                    <span class="flex items-center gap-2">
                        <vx-badge color="slate" size="xs" class="font-mono">{{ details.data.id }}</vx-badge>
                        {{ details.data.name }}
                    </span>

                    <template v-slot:actions>
                        <vx-button
                            :href="`/kiosk/inbound-text/links/${props.linkId}/edit`"
                            size="lg"
                            color="muted-dark"
                            data-test="edit"
                        >
                            Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                        </vx-button>
                        <vx-button @click="destroy()" size="lg" color="danger" outline data-test="delete">
                            Delete <font-awesome-icon widthAuto :icon="faTrash" />
                        </vx-button>
                    </template>
                </page-header>

                <div class="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
                    <vx-expandable-card>
                        <template v-slot:content>
                            <dl class="grid grid-cols-1 gap-6 text-sm sm:grid-cols-2">
                                <div>
                                    <dt class="font-bold">Name</dt>
                                    <dd class="text-slate-500" data-test="name">{{ details.data.name }}</dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Phone</dt>
                                    <dd class="text-slate-500" data-test="phone">{{ details.data.phone }}</dd>
                                </div>
                                <div>
                                    <dt class="font-bold">Slug</dt>
                                    <dd class="font-mono text-slate-500" data-test="slug">{{ details.slug }}</dd>
                                </div>
                                <div>
                                    <dt class="font-bold">URL</dt>
                                    <dd class="break-all">
                                        <a
                                            :href="details.url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-test="url"
                                            >{{ details.url }}</a
                                        >
                                    </dd>
                                </div>
                                <div class="sm:col-span-2">
                                    <dt class="font-bold">Fallback URL</dt>
                                    <dd class="break-all">
                                        <a
                                            :href="details.data.fallback_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-test="fallback-url"
                                            >{{ details.data.fallback_url }}</a
                                        >
                                    </dd>
                                </div>
                                <div class="sm:col-span-2">
                                    <dt class="font-bold">SMS Body</dt>
                                    <dd class="whitespace-pre-line text-slate-500" data-test="body">
                                        {{ details.data.body }}
                                    </dd>
                                </div>
                            </dl>
                        </template>
                    </vx-expandable-card>

                    <vx-expandable-card>
                        <template v-slot:content>
                            <div class="flex flex-col items-center text-center">
                                <div class="text-xl font-extrabold">QR Code</div>
                                <img
                                    :src="qrcodeSrc"
                                    :alt="`QR code for ${details.slug}`"
                                    class="mt-4 h-48 w-48 rounded-xl bg-white p-2 ring-1 ring-slate-200"
                                    data-test="qrcode"
                                />
                                <a
                                    :href="qrcodeSrc"
                                    :download="`${details.slug}.png`"
                                    class="mt-4 text-sm"
                                    data-test="download-qrcode"
                                >
                                    Download QR Code
                                </a>
                            </div>
                        </template>
                    </vx-expandable-card>
                </div>
            </template>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faFilePen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { isAxiosError } from 'axios';
import { computed, ref } from 'vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import { useCallout } from '~/composables/useCallout';
import { adminInboundTextClient } from '~/services';
import type { InboundTextLinkDetails } from '~/services/adminInboundTextClient.types';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import dayjs from '~/utils/dayjs';
import { escapeHtml } from '~/utils/html';
import { throwSwalRequestError } from '~/utils/request';

const props = defineProps<{ linkId: number }>();

const callout = useCallout();

const details = ref<InboundTextLinkDetails | null>(null);
const loading = ref(true);
const loadError = ref('');

const qrcodeSrc = computed(() => `data:image/png;base64,${details.value?.qrcode}`);

const load = async () => {
    try {
        const response = await adminInboundTextClient.getLink(props.linkId);
        details.value = response.data;
    } catch (error) {
        loadError.value =
            isAxiosError(error) && HTTP_NOT_FOUND === error.response?.status
                ? 'Link not found.'
                : 'Failed to load link. Please try again.';
    } finally {
        loading.value = false;
    }
};

const destroy = () =>
    VxSwal.confirmation('Delete Link', 'Are you sure? There is no undo.', 'danger', () =>
        adminInboundTextClient
            .deleteLink(props.linkId)
            .then(() => {
                callout.success(
                    'kiosk-inbound-text-links',
                    `<strong>${escapeHtml(details.value?.data.name ?? '')}</strong> deleted`,
                    {
                        ttl: dayjs().add(30, 'seconds'),
                        shadow: true,
                    }
                );

                window.location.assign('/kiosk/inbound-text/links');
            })
            .catch(throwSwalRequestError)
    );

load();
</script>
